import React from "react";
import PropTypes from "prop-types";
import {Chip, useTheme} from "@mui/material";
import CheckBox from "@mui/icons-material/CheckBox";
import ReportProblem from "@mui/icons-material/ReportProblem";
import SAMLIcon from "../../../resources/icons/SAMLIcon";
import {CenteredBox} from "../../../components/CenteredBox";
import {useNavigate} from "react-router-dom";
import StyledTooltip from "../../../components/tooltips/StyledTooltip";
import {useIntegrationStyles} from "../IntegrationStyles";
import {useIntegrations} from "../Context/IntegrationContext";
import {find} from "underscore";

const isConfigEnabled = (configuration) =>
  configuration?.isEnabled === true || configuration?.isEnabled === "true";

export const IntegrationCard = ({integration}) => {
  const {classes} = useIntegrationStyles();
  const {integrations} = useIntegrations();
  const navigate = useNavigate();
  const theme = useTheme();

  const logo =
    theme.palette.mode === "dark" ? (integration?.logoDark ?? integration?.logo) : integration.logo;

  const isDeprecatedSlackConfigured =
    integration?.name === "slack" && !!integration?.configuration?.identifier;

  const newSlackEnabled = isConfigEnabled(find(integrations, {name: "slackV2"})?.configuration);

  // TODO(msteams-v1): when the v1 webhook integration is removed, delete this block and the
  // Microsoft Teams terms below.
  const isMSTeamsCard = integration?.name === "msteams";
  const isLegacyMSTeamsConfigured = isMSTeamsCard && !!integration?.configuration?.webhook;
  const isMSTeamsV2Enabled =
    isMSTeamsCard && isConfigEnabled(find(integrations, {name: "msteamsv2"})?.configuration);

  const showEnabled =
    integration?.name === "slack" ?
      isConfigEnabled(integration?.configuration) || newSlackEnabled :
      isConfigEnabled(integration?.configuration) || isMSTeamsV2Enabled;
  const getIntegrationPath = () => {
    if (integration?.name === "slack" && !isDeprecatedSlackConfigured) {
      return "slackV2";
    }
    if (isMSTeamsCard && !isLegacyMSTeamsConfigured) {
      return "msteamsv2";
    }
    return integration.name;
  };
  const integrationPath = getIntegrationPath();

  return (
    <CenteredBox
      id={`integration-${integration.name}`}
      aria-label={`${integration.title} configuration`}
      className={classes.card}
      onClick={() => {
        navigate(`/integrations/${integrationPath}`);
      }}
    >
      {showEnabled && (
        <StyledTooltip title={`${integration?.title} is currently enabled`}>
          <CheckBox className={classes.isEnabledIcon} color="secondary" />
        </StyledTooltip>
      )}
      {isDeprecatedSlackConfigured && (
        <StyledTooltip
          title={
            "This Slack integration is deprecated. " +
            "Open this integration to migrate to the new one."
          }
        >
          <ReportProblem
            aria-label="Legacy Slack integration is configured"
            sx={{
              position: "absolute",
              top: 5,
              left: 5,
              color: theme.palette.warning.main,
            }}
          />
        </StyledTooltip>
      )}
      {isLegacyMSTeamsConfigured && (
        <StyledTooltip
          title={
            "This Microsoft Teams integration is deprecated. " +
            "Open this integration to migrate to the new one."
          }
        >
          <ReportProblem
            aria-label="Legacy Microsoft Teams integration is configured"
            sx={{
              position: "absolute",
              top: 5,
              left: 5,
              color: theme.palette.warning.main,
            }}
          />
        </StyledTooltip>
      )}
      {integration?.isNew && !isDeprecatedSlackConfigured && !isLegacyMSTeamsConfigured && (
        <Chip
          label="NEW"
          color="primary"
          size="small"
          sx={{position: "absolute", top: 5, left: 5, fontWeight: "bold", fontSize: "0.65rem"}}
        />
      )}
      {integration.logo === "GENERICSAML" ?
        (
          <SAMLIcon text="SAML Connector" fontSize={18} fontWeight={600} y="50%" />
        ) :
        (
          <img
            className={classes.logo}
            src={logo}
            style={{maxWidth: integration?.configuration?.logoMaxWidth}}
          />
        )}
    </CenteredBox>
  );
};

IntegrationCard.propTypes = {
  integration: PropTypes.shape({
    name: PropTypes.string,
    title: PropTypes.string,
    logo: PropTypes.string,
    logoDark: PropTypes.string,
    backgroundColor: PropTypes.string,
    configuration: PropTypes.object,
    isNew: PropTypes.bool,
  }),
};
